import { api } from './api.js';
import type { ApiResponse } from '@elmes/shared';
import type { ModuleManifest, PageSpec, WidgetSpec } from '@elmes/shared/plugin/manifest';

export type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal';

export interface LogEntry {
  level: LogLevel;
  levelValue: number;
  msg: string;
  time: number;
  raw: string;
  reqId?: string;
  module?: string;
  fields: Record<string, unknown>;
}

export interface LogsResponse {
  entries: LogEntry[];
  total: number;
  cursor: number;
  fileSize: number;
}

export interface LogsQuery {
  limit?: number;
  since?: number;
  level?: LogLevel;
  filter?: string;
  reqId?: string;
  module?: string;
}

export interface Pm2Status {
  name: string;
  status?: string;
  cpu?: number;
  memory?: number;
  restarts?: number;
  uptime: number;
  pid?: number;
  pmId?: number;
  execMode?: string;
  nodeVersion?: string;
}

export interface Pm2Metrics extends Pm2Status {
  memoryMB: number;
  unstableRestarts?: number;
  watching?: boolean;
  maxMemoryRestart?: string;
  errorLogPath?: string;
  outLogPath?: string;
  scriptPath?: string;
  createdAt?: number;
  outLogSize: number;
  errLogSize: number;
}

export interface HostHealth {
  hostname: string;
  platform: string;
  arch: string;
  uptimeSeconds: number;
  cpuCount: number;
  loadAvg: { '1m': number; '5m': number; '15m': number };
  memory: { totalBytes: number; freeBytes: number; usedBytes: number; usagePercent: number };
  nodeVersion: string;
  processMemory: { rss: number; heapTotal: number; heapUsed: number; external: number; arrayBuffers: number };
}

export interface DiskHealth {
  filesystem: string;
  sizeKb: number;
  usedKb: number;
  availKb: number;
  usagePercent: number;
  mountedOn: string;
}

export interface RedisHealth {
  status: 'up' | 'down';
  pingMs?: number;
  usedMemoryBytes?: number;
  usedMemoryHuman?: string;
  connectedClients?: number;
  totalCommandsProcessed?: number;
  keyspaceHits?: number;
  keyspaceMisses?: number;
  hitRate?: number;
  evictedKeys?: number;
  uptimeSeconds?: number;
  error?: string;
}

export interface MariaDbHealth {
  status: 'up' | 'down';
  pingMs?: number;
  threadsConnected?: number;
  threadsRunning?: number;
  uptimeSeconds?: number;
  slowQueries?: number;
  questions?: number;
  bytesSent?: number;
  bytesReceived?: number;
  innodbBufferPoolPagesTotal?: number;
  innodbBufferPoolPagesFree?: number;
  version?: string;
  error?: string;
}

export interface CombinedHealth {
  host: HostHealth;
  disk: DiskHealth | null;
  redis: RedisHealth;
  mariadb: MariaDbHealth;
  timestamp: string;
}

export interface RunbookItem {
  id: number;
  slug: string;
  title: string;
  type: string;
  summary: string | null;
  updatedAt: string;
}

export interface PluginSummary {
  slug: string;
  name: string;
  /** Short description from manifest — single source of truth (issue #322). */
  description: string;
  version: string;
  category: string;
  status: string;
  source: string;
  manifestHash: string;
  capabilitiesCount: number;
  hasAiSurface: boolean;
  widgets: WidgetSpec[];
  pagesCount: number;
  /** Pages this plugin registers (Faz 6 dynamic routing). */
  pages: PageSpec[];
  installedAt: string;
  updatedAt: string;
  // Manifest UI fields for useModules hook (issue #322)
  /** Route prefix / path for the module (from manifest.routePrefix). */
  routePath: string | null;
  /** Lucide icon name (from manifest.icon.name). */
  iconName: string | null;
  /** Minimum role required to access this module. */
  requiredRole: string | null;
  /** Fine-grained permission slugs required beyond role. */
  requiredPermissions: string[];
  /** Whether this module should appear on the dashboard. */
  showOnDashboard: boolean;
  /** Dashboard display order. */
  dashboardOrder: number | null;
}

export interface PluginDetail extends PluginSummary {
  manifestJson: ModuleManifest;
}

export interface PluginsResponse {
  data: PluginSummary[];
  count: number;
}

export const systemService = {
  async getLogs(query: LogsQuery = {}): Promise<LogsResponse> {
    const params: Record<string, string | number> = {};
    if (query.limit !== undefined) params.limit = query.limit;
    if (query.since !== undefined) params.since = query.since;
    if (query.level) params.level = query.level;
    if (query.filter) params.filter = query.filter;
    if (query.reqId) params.reqId = query.reqId;
    if (query.module) params.module = query.module;
    const res = await api.get<ApiResponse<LogsResponse>>('/system/logs', { params });
    return res.data.data!;
  },

  async getPm2Status(): Promise<Pm2Status | null> {
    const res = await api.get<ApiResponse<Pm2Status | null>>('/system/pm2/status');
    return res.data.data ?? null;
  },

  async getPm2Metrics(): Promise<Pm2Metrics | null> {
    const res = await api.get<ApiResponse<Pm2Metrics | null>>('/system/pm2/metrics');
    return res.data.data ?? null;
  },

  async getStreamToken(): Promise<string> {
    const res = await api.post<ApiResponse<{ token: string; expiresInSeconds: number }>>(
      '/system/logs/stream-token',
    );
    return res.data.data!.token;
  },

  buildStreamUrl(token: string): string {
    // api client baseURL'i /api → EventSource için absolute URL gerekli
    const base = (api.defaults.baseURL ?? '').replace(/\/$/, '');
    return `${base}/system/logs/stream?token=${encodeURIComponent(token)}`;
  },

  async getHealth(): Promise<CombinedHealth> {
    const res = await api.get<ApiResponse<CombinedHealth>>('/system/health');
    return res.data.data!;
  },

  async runPm2Action(action: 'restart' | 'reload'): Promise<void> {
    await api.post<ApiResponse<unknown>>('/system/pm2/action', { action });
  },

  async listRunbooks(): Promise<RunbookItem[]> {
    const res = await api.get<ApiResponse<{ items: RunbookItem[] }>>('/system/runbooks');
    return res.data.data?.items ?? [];
  },

  async listPlugins(): Promise<PluginsResponse> {
    const res = await api.get<PluginsResponse>('/system/modules');
    return res.data;
  },

  async getPlugin(slug: string): Promise<PluginDetail> {
    const res = await api.get<ApiResponse<PluginDetail>>(`/system/modules/${encodeURIComponent(slug)}`);
    return res.data.data!;
  },

  async enablePlugin(slug: string): Promise<{ slug: string; status: string; updatedAt: string }> {
    const res = await api.post<ApiResponse<{ slug: string; status: string; updatedAt: string }>>(
      `/system/modules/${encodeURIComponent(slug)}/enable`,
    );
    return res.data.data!;
  },

  async disablePlugin(slug: string): Promise<{ slug: string; status: string; updatedAt: string }> {
    const res = await api.post<ApiResponse<{ slug: string; status: string; updatedAt: string }>>(
      `/system/modules/${encodeURIComponent(slug)}/disable`,
    );
    return res.data.data!;
  },

  async reloadPm2(): Promise<{ status: string; processName: string }> {
    const res = await api.post<ApiResponse<{ status: string; processName: string }>>(
      '/system/pm2-reload',
    );
    return res.data.data!;
  },
};
